// นิติกรณ์กรมการกงสุล × ประเภทเอกสาร — landing data (TH/EN)
// อ้างอิงกรอบงาน: กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th)
// ค่าธรรมเนียมราชการ: 200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วนภายในวันเดียว)
// สถานะ Apostille: ไทยยื่นภาคยานุวัติสาร 30 มิ.ย. 2569 (2026) — อนุสัญญามีผลกับไทย 28 ก.พ. 2570 (2027) — hcch.net

export interface ConsularDocLanding {
  slug: string;
  titleTh: string;
  titleEn: string;
  /** ชื่อ/แบบฟอร์มทางการและหน่วยงานผู้ออก */
  issuerTh: string;
  issuerEn: string;
  /** ลำดับชั้นการรับรองก่อนถึงกรมการกงสุล */
  chainTh: string[];
  chainEn: string[];
  useCaseTh: string;
  useCaseEn: string;
  prepareTh: string[];
  prepareEn: string[];
  pitfallsTh: string[];
  pitfallsEn: string[];
  apostilleNoteTh: string;
  apostilleNoteEn: string;
  turnaroundTh: string;
  turnaroundEn: string;
}

const D = (d: ConsularDocLanding): ConsularDocLanding => d;

const COMMON_TURN_TH =
  "แปล 1–2 วันทำการ · นิติกรณ์แบบปกติ 2–3 วันทำการ (200 บาท/ตรา) หรือแบบด่วนรับวันเดียวหากยื่นก่อน 11:30 น. (400 บาท/ตรา) · คิวสถานทูตปลายทางแยกต่างหาก";
const COMMON_TURN_EN =
  "1–2 working days for translation; normal MFA legalisation takes 2–3 working days (THB 200 per stamp) or same-day express if filed before 11:30 (THB 400 per stamp). Destination embassy queues are separate.";
const APO_TH =
  "จนถึงวันที่ 28 กุมภาพันธ์ 2570 ประเทศไทยยังใช้เส้นทางรับรองสองชั้น (กรมการกงสุล → สถานทูตปลายทางในไทย) หลังอนุสัญญากรุงเฮกมีผลกับไทย เอกสารที่ส่งไปประเทศภาคีจะใช้ Apostille ใบเดียวแทนขั้นสถานทูตได้ ส่วนประเทศนอกภาคี เช่น จีนแผ่นดินใหญ่บางกรณี เวียดนาม และกลุ่มตะวันออกกลางบางประเทศ ยังต้องรับรองสถานทูตเช่นเดิม";
const APO_EN =
  "Until 28 February 2027 Thailand still uses the two-step chain (Department of Consular Affairs, then the destination embassy in Thailand). Once the Hague Convention enters into force for Thailand, a single Apostille replaces the embassy step for member states; non-member destinations still require embassy legalisation.";

export const CONSULAR_DOC_LANDINGS: ConsularDocLanding[] = [
  D({
    slug: "birth-certificate",
    titleTh: "รับรองนิติกรณ์สูติบัตร",
    titleEn: "Legalisation of a Thai Birth Certificate",
    issuerTh: "สูติบัตร ท.ร.1 / ท.ร.19 ฉบับคัดรับรองจากสำนักทะเบียนอำเภอหรือสำนักงานเขต",
    issuerEn: "Birth certificate (Tor Ror 1 / Tor Ror 19) certified by the district registrar",
    chainTh: [
      "คัดสำเนารับรองจากอำเภอ/เขตผู้ออกเอกสาร",
      "แปลไทย–อังกฤษพร้อมคำรับรองความถูกต้องของผู้แปล",
      "ยื่นนิติกรณ์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล แจ้งวัฒนะ",
      "รับรองต่อที่สถานทูต/สถานกงสุลปลายทางในไทย (หรือ Apostille ตั้งแต่ 28 ก.พ. 2570)",
    ],
    chainEn: [
      "Obtain a certified extract from the issuing district office",
      "Thai–English translation with the translator's certificate of accuracy",
      "File for legalisation at the Department of Consular Affairs, Chaeng Watthana",
      "Then legalise at the destination embassy in Thailand (or use an Apostille from 28 February 2027)",
    ],
    useCaseTh:
      "ใช้ยื่นวีซ่าติดตามครอบครัว ขอสัญชาติหรือหนังสือเดินทางต่างประเทศให้บุตร บันทึกการเกิดที่ทะเบียนราษฎรต่างประเทศ สมัครเรียน และเรื่องมรดก",
    useCaseEn:
      "Dependent visas, foreign citizenship or passport applications for a child, foreign civil registry filings, school enrolment and inheritance matters.",
    prepareTh: [
      "สูติบัตรฉบับคัดรับรองใหม่ (ปลายทางหลายแห่งรับเฉพาะที่ออกไม่เกิน 3–6 เดือน)",
      "สำเนาหนังสือเดินทางเด็กและบิดามารดา เพื่อล็อกตัวสะกดชื่อ",
      "หนังสือมอบอำนาจติดอากรแสตมป์ 10 บาท หากให้ผู้อื่นยื่นแทน",
    ],
    prepareEn: [
      "A freshly certified extract (many authorities accept only 3–6 month old copies)",
      "Passport copies of the child and both parents to lock name spellings",
      "A power of attorney with a THB 10 duty stamp if an agent files on your behalf",
    ],
    pitfallsTh: [
      "ยื่นสำเนาถ่ายเอกสารที่ไม่มีตราประทับนายทะเบียน — กรมการกงสุลไม่รับ",
      "คำแปลสะกดชื่อไม่ตรงหนังสือเดินทาง",
      "ไม่ได้แปลตราประทับและลายมือชื่อนายทะเบียนด้านหลังเอกสาร",
    ],
    pitfallsEn: [
      "Submitting a plain photocopy without the registrar's seal",
      "Name spellings in the translation that differ from the passport",
      "Leaving seals and the registrar's signature untranslated",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "marriage-certificate",
    titleTh: "รับรองนิติกรณ์ทะเบียนสมรส (คร.2 / คร.3)",
    titleEn: "Legalisation of a Thai Marriage Certificate",
    issuerTh: "ใบสำคัญการสมรส (คร.3) และทะเบียนสมรส (คร.2) ฉบับคัดรับรองจากอำเภอ/เขต",
    issuerEn: "Marriage certificate (Kor Ror 3) and marriage register extract (Kor Ror 2)",
    chainTh: [
      "คัด คร.2 ฉบับรับรองจากอำเภอ/เขตที่จดทะเบียน",
      "แปลพร้อมคำรับรองผู้แปล",
      "นิติกรณ์กรมการกงสุล",
      "รับรองสถานทูตปลายทาง หรือ Apostille เมื่ออนุสัญญามีผลกับไทย",
    ],
    chainEn: [
      "Obtain a certified Kor Ror 2 extract from the registering district office",
      "Translation with certificate of accuracy",
      "MFA legalisation",
      "Destination embassy legalisation, or an Apostille once the Convention applies to Thailand",
    ],
    useCaseTh:
      "ใช้ยื่นวีซ่าคู่สมรส ขอสิทธิพำนักในต่างประเทศ บันทึกทะเบียนสมรสที่ประเทศคู่สมรส เปลี่ยนนามสกุลในเอกสารต่างประเทศ และเรื่องภาษี/ประกันสังคม",
    useCaseEn:
      "Spouse visas, residence applications, registering the marriage in the spouse's country, foreign name changes, and tax or social-security filings.",
    prepareTh: [
      "คร.2 ฉบับคัดรับรอง (แนะนำออกใหม่ภายใน 6 เดือน)",
      "สำเนาหนังสือเดินทาง/บัตรประชาชนของทั้งสองฝ่าย",
      "ใบเปลี่ยนชื่อ-สกุล หากมีการเปลี่ยนหลังสมรส",
    ],
    prepareEn: [
      "A certified Kor Ror 2 extract (ideally issued within 6 months)",
      "Passport or ID copies of both spouses",
      "Name-change certificates if the name changed after marriage",
    ],
    pitfallsTh: [
      "ยื่นเฉพาะ คร.3 ซึ่งบางปลายทางถือว่าไม่ใช่สาระสำคัญครบถ้วน ควรยื่น คร.2 ควบคู่",
      "ชื่อคู่สมรสต่างชาติสะกดไม่ตรงหนังสือเดินทาง",
      "ทะเบียนสมรสเก่ามากจนตัวอักษรไม่ชัด ควรคัดใหม่ก่อน",
    ],
    pitfallsEn: [
      "Filing only the Kor Ror 3 when the receiving authority expects the fuller Kor Ror 2 register extract",
      "Foreign spouse name spelled differently from the passport",
      "An old, faded certificate — request a fresh extract first",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "divorce-certificate",
    titleTh: "รับรองนิติกรณ์ใบหย่า (คร.6 / คร.7)",
    titleEn: "Legalisation of a Thai Divorce Certificate",
    issuerTh: "ใบสำคัญการหย่า (คร.7) ทะเบียนการหย่า (คร.6) หรือคำพิพากษาศาลพร้อมหนังสือรับรองคดีถึงที่สุด",
    issuerEn: "Divorce certificate (Kor Ror 7), divorce register (Kor Ror 6), or a court judgment with a certificate of finality",
    chainTh: [
      "คัด คร.6/คร.7 จากอำเภอ หรือคัดคำพิพากษาพร้อมหนังสือรับรองคดีถึงที่สุดจากศาล",
      "แปลพร้อมคำรับรองผู้แปล",
      "นิติกรณ์กรมการกงสุล",
      "รับรองสถานทูตปลายทาง หรือ Apostille หลัง 28 ก.พ. 2570",
    ],
    chainEn: [
      "Obtain Kor Ror 6/7 from the district office, or the judgment plus certificate of finality from the court",
      "Translation with certificate of accuracy",
      "MFA legalisation",
      "Destination embassy, or Apostille after 28 February 2027",
    ],
    useCaseTh: "ใช้ยืนยันสถานะโสดก่อนสมรสใหม่ในต่างประเทศ ยื่นวีซ่า แบ่งทรัพย์สิน และเรื่องสิทธิปกครองบุตร",
    useCaseEn: "Proving marital status before remarrying abroad, visa filings, property division and child-custody matters.",
    prepareTh: [
      "คร.6 ฉบับคัดรับรอง (มีรายละเอียดข้อตกลงมากกว่า คร.7)",
      "คำพิพากษาและหนังสือรับรองคดีถึงที่สุด กรณีหย่าโดยคำพิพากษา",
      "หนังสือรับรองความเป็นโสดจากอำเภอ หากปลายทางร้องขอเพิ่ม",
    ],
    prepareEn: [
      "A certified Kor Ror 6 (it contains more of the settlement detail than Kor Ror 7)",
      "The judgment and certificate of finality for court divorces",
      "A single-status certificate from the district office if the destination also asks for one",
    ],
    pitfallsTh: [
      "หย่าโดยคำพิพากษาแต่ยื่นเฉพาะคำพิพากษาโดยไม่มีหนังสือรับรองคดีถึงที่สุด",
      "ยังไม่ได้บันทึกการหย่าที่อำเภอ ทำให้ทะเบียนราษฎรยังแสดงสถานะสมรส",
      "ปลายทางบางประเทศต้องการทั้งใบหย่าและหนังสือรับรองความเป็นโสด",
    ],
    pitfallsEn: [
      "Filing a judgment without the certificate of finality",
      "The divorce not yet recorded at the district office, so the civil registry still shows 'married'",
      "Some destinations require both the divorce record and a single-status certificate",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "single-status",
    titleTh: "รับรองนิติกรณ์หนังสือรับรองความเป็นโสด",
    titleEn: "Legalisation of a Thai Single-Status Certificate",
    issuerTh: "หนังสือรับรองความเป็นโสด/สถานภาพสมรส ออกโดยสำนักทะเบียนอำเภอหรือสำนักงานเขต",
    issuerEn: "Single-status (marital status) certificate issued by the district registrar",
    chainTh: [
      "ขอหนังสือรับรองที่อำเภอตามทะเบียนบ้าน (พยานประกอบตามที่นายทะเบียนกำหนด)",
      "แปลพร้อมคำรับรองผู้แปล",
      "นิติกรณ์กรมการกงสุล",
      "รับรองสถานทูตปลายทาง หรือ Apostille หลังอนุสัญญามีผล",
    ],
    chainEn: [
      "Request the certificate at the district office of your house registration (with witnesses if the registrar requires)",
      "Translation with certificate of accuracy",
      "MFA legalisation",
      "Destination embassy, or Apostille once in force",
    ],
    useCaseTh: "ใช้จดทะเบียนสมรสในต่างประเทศ ยื่น Ondertrouw/Ehefähigkeitszeugnis และยื่นวีซ่าคู่หมั้น",
    useCaseEn: "Marrying abroad, filing Ondertrouw or marriage-capacity procedures, and fiancé(e) visas.",
    prepareTh: [
      "บัตรประชาชนและทะเบียนบ้านฉบับจริง",
      "พยานบุคคลตามที่อำเภอกำหนด (บางอำเภอขอ 2 คน)",
      "กำหนดวันพิธี เพื่อไม่ให้เอกสารหมดอายุก่อนใช้",
    ],
    prepareEn: [
      "Original Thai ID card and house registration",
      "Witnesses if the district office requires them (often two)",
      "Your intended ceremony date, so the certificate does not expire before use",
    ],
    pitfallsTh: [
      "รับรองล่วงหน้านานเกินไป — ปลายทางส่วนใหญ่รับเอกสารอายุไม่เกิน 3–6 เดือน",
      "ข้อความในหนังสือไม่ได้ระบุว่า 'ไม่เคยสมรส' หรือ 'หย่าแล้ว' ตามที่ปลายทางต้องการ",
      "ไม่ได้แนบใบหย่าประกอบ กรณีเคยสมรสมาก่อน",
    ],
    pitfallsEn: [
      "Legalising too early — most destinations accept documents no older than 3–6 months",
      "Wording that does not state 'never married' or 'divorced' as the destination requires",
      "Not attaching the divorce record when there was a previous marriage",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "house-registration",
    titleTh: "รับรองนิติกรณ์ทะเบียนบ้านและบัตรประชาชน",
    titleEn: "Legalisation of a Thai House Registration & ID Card",
    issuerTh: "ทะเบียนบ้าน (ท.ร.14) ฉบับคัดรับรอง และสำเนาบัตรประจำตัวประชาชนรับรองโดยนายทะเบียน",
    issuerEn: "House registration (Tor Ror 14) certified extract and a registrar-certified ID card copy",
    chainTh: [
      "คัดรับรองทะเบียนบ้าน/บัตรประชาชนที่อำเภอหรือเขต",
      "แปลพร้อมคำรับรองผู้แปล",
      "นิติกรณ์กรมการกงสุล",
      "รับรองสถานทูตปลายทาง หรือ Apostille หลัง 28 ก.พ. 2570",
    ],
    chainEn: [
      "Get a certified copy at the district office",
      "Translation with certificate of accuracy",
      "MFA legalisation",
      "Destination embassy, or Apostille after 28 February 2027",
    ],
    useCaseTh: "ใช้พิสูจน์ที่อยู่และตัวตนสำหรับเปิดบัญชีธนาคารต่างประเทศ ยื่นวีซ่า ลงทะเบียนพำนัก และงานภาษี",
    useCaseEn: "Proof of address and identity for foreign bank accounts, visas, residence registration and tax matters.",
    prepareTh: [
      "ทะเบียนบ้านฉบับคัดรับรองใหม่จากอำเภอ",
      "สำเนาบัตรประชาชนที่นายทะเบียนรับรอง (สำเนาที่เจ้าตัวเซ็นเองไม่พอสำหรับนิติกรณ์บางกรณี)",
      "หนังสือเดินทาง เพื่ออ้างอิงตัวสะกดชื่อ",
    ],
    prepareEn: [
      "A freshly certified house registration extract",
      "A registrar-certified ID copy (a self-certified copy is not always accepted)",
      "Your passport for name-spelling reference",
    ],
    pitfallsTh: [
      "ใช้ทะเบียนบ้านเล่มจริงที่ยังไม่ได้คัดรับรอง",
      "ที่อยู่ในทะเบียนบ้านไม่ตรงกับเอกสารอื่นที่ยื่นคู่กัน",
      "แปลเลขที่บ้าน/ตำบลไม่ตรงระบบถอดอักษรที่ใช้ในเอกสารชุดเดียวกัน",
    ],
    pitfallsEn: [
      "Using the original booklet without a certified extract",
      "An address that conflicts with the other documents in the bundle",
      "Inconsistent romanisation of the address across the bundle",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "degree-transcript",
    titleTh: "รับรองนิติกรณ์ปริญญาบัตรและทรานสคริปต์",
    titleEn: "Legalisation of Thai Degrees & Transcripts",
    issuerTh: "ปริญญาบัตร ใบรับรองสำเร็จการศึกษา และใบแสดงผลการเรียน ออกโดยสถาบันการศึกษา",
    issuerEn: "Degree certificate, graduation letter and academic transcript issued by the institution",
    chainTh: [
      "ขอฉบับภาษาอังกฤษหรือฉบับรับรองสำเนาถูกต้องจากทะเบียนของมหาวิทยาลัย",
      "บางกรณีต้องผ่านการรับรองจากกระทรวง อว./สพฐ. ตามที่ปลายทางกำหนด",
      "นิติกรณ์กรมการกงสุล (รับรองลายมือชื่อผู้มีอำนาจลงนาม)",
      "รับรองสถานทูตปลายทาง หรือ Apostille หลังอนุสัญญามีผล",
    ],
    chainEn: [
      "Request an English version or a registrar-certified copy from the university",
      "Where required, obtain ministry verification before consular filing",
      "MFA legalisation (verifying the authorised signature)",
      "Destination embassy, or Apostille once in force",
    ],
    useCaseTh: "ใช้สมัครเรียนต่อ ขอใบอนุญาตประกอบวิชาชีพ ยื่นวีซ่าทำงาน และประเมินวุฒิ (credential evaluation)",
    useCaseEn: "Further study, professional licensing, work visas and credential evaluation.",
    prepareTh: [
      "ปริญญาบัตร/ทรานสคริปต์ฉบับที่มหาวิทยาลัยรับรอง พร้อมชื่อและตำแหน่งผู้ลงนาม",
      "ตรวจสอบว่าปลายทางต้องการซองปิดผนึกจากสถาบันหรือไม่",
      "สำเนาหนังสือเดินทางเพื่อยืนยันตัวสะกดชื่อ",
    ],
    prepareEn: [
      "University-certified degree/transcript showing the signatory's name and position",
      "Check whether the destination requires a sealed envelope from the institution",
      "Passport copy for name spelling",
    ],
    pitfallsTh: [
      "ลายมือชื่อผู้ลงนามไม่อยู่ในฐานตัวอย่างลายมือชื่อที่กรมการกงสุลตรวจสอบได้",
      "ใช้สำเนาถ่ายที่ไม่มีตราประทับสถาบัน",
      "ทรานสคริปต์ฉบับนักศึกษา (student copy) แทนฉบับทางการ",
    ],
    pitfallsEn: [
      "A signatory whose specimen signature the MFA cannot verify",
      "A photocopy without the institution's seal",
      "Submitting a student copy instead of the official transcript",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "police-clearance",
    titleTh: "รับรองนิติกรณ์หนังสือรับรองความประพฤติ",
    titleEn: "Legalisation of a Thai Police Clearance Certificate",
    issuerTh: "หนังสือรับรองความประพฤติ ออกโดยศูนย์บริการออกหนังสือรับรองความประพฤติ สำนักงานตำรวจแห่งชาติ",
    issuerEn: "Police Clearance Certificate issued by the Royal Thai Police Criminal Records Division",
    chainTh: [
      "ยื่นพิมพ์ลายนิ้วมือและขอหนังสือรับรองความประพฤติจากสำนักงานตำรวจแห่งชาติ",
      "นิติกรณ์กรมการกงสุล",
      "รับรองสถานทูตปลายทาง หรือ Apostille หลัง 28 ก.พ. 2570",
    ],
    chainEn: [
      "Submit fingerprints and apply to the Royal Thai Police",
      "MFA legalisation",
      "Destination embassy, or Apostille after 28 February 2027",
    ],
    useCaseTh: "ใช้ยื่นวีซ่าถาวร วีซ่าทำงาน ขอสัญชาติ และสมัครงานที่ต้องตรวจประวัติอาชญากรรม",
    useCaseEn: "Permanent residence, work visas, citizenship applications and employment background checks.",
    prepareTh: [
      "หนังสือรับรองฉบับจริงที่ระบุประเทศ/วัตถุประสงค์ปลายทางให้ตรง",
      "หนังสือเดินทางฉบับจริงและสำเนา",
      "แผ่นลายนิ้วมือที่รับรองโดยเจ้าหน้าที่ตำรวจ",
    ],
    prepareEn: [
      "The original certificate naming the correct destination country/purpose",
      "Original passport and copies",
      "Police-certified fingerprint card",
    ],
    pitfallsTh: [
      "หนังสือระบุประเทศปลายทางผิด ทำให้ต้องขอใหม่ทั้งชุด",
      "เอกสารเกินอายุที่ปลายทางกำหนด (ส่วนใหญ่ 3–6 เดือน)",
      "ยื่นสำเนาแทนฉบับจริงเข้าสู่ขั้นนิติกรณ์",
    ],
    pitfallsEn: [
      "The certificate naming the wrong destination, forcing a fresh application",
      "Exceeding the destination's validity window (usually 3–6 months)",
      "Filing a copy instead of the original for legalisation",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh:
      "หนังสือรับรองความประพฤติออกโดยตำรวจประมาณ 2–4 สัปดาห์ · นิติกรณ์เพิ่ม 2–3 วันทำการ (ปกติ) หรือวันเดียวแบบด่วน",
    turnaroundEn:
      "The police certificate itself takes roughly 2–4 weeks; MFA legalisation adds 2–3 working days, or same day for express.",
  }),
  D({
    slug: "power-of-attorney",
    titleTh: "รับรองนิติกรณ์หนังสือมอบอำนาจ",
    titleEn: "Legalisation of a Power of Attorney",
    issuerTh: "หนังสือมอบอำนาจที่ลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ของสภาทนายความ",
    issuerEn: "A power of attorney signed before a Notarial Services Attorney licensed by the Lawyers Council of Thailand",
    chainTh: [
      "ร่างหนังสือมอบอำนาจให้ตรงข้อกำหนดของหน่วยงานผู้รับปลายทาง",
      "ลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร",
      "นิติกรณ์กรมการกงสุล (รับรองลายมือชื่อทนายที่ขึ้นทะเบียน)",
      "รับรองสถานทูตปลายทาง หรือ Apostille หลังอนุสัญญามีผลกับไทย",
    ],
    chainEn: [
      "Draft the POA to the receiving authority's exact requirements",
      "Sign before a Notarial Services Attorney",
      "MFA legalisation verifying the registered attorney's signature",
      "Destination embassy, or Apostille once the Convention applies",
    ],
    useCaseTh: "ใช้ขายหรือโอนอสังหาริมทรัพย์ในต่างประเทศ ดำเนินคดี จัดการมรดก ปิดบัญชีธนาคาร และงานนิติบุคคล",
    useCaseEn: "Selling or transferring property abroad, litigation, estate administration, closing bank accounts and corporate filings.",
    prepareTh: [
      "แบบฟอร์มหรือถ้อยคำที่หน่วยงานปลายทางกำหนด (ธนาคาร/ทะเบียนที่ดิน/ศาล มักมีแบบเฉพาะ)",
      "หนังสือเดินทางฉบับจริงของผู้มอบอำนาจ",
      "รายละเอียดผู้รับมอบอำนาจและขอบเขตอำนาจที่ชัดเจน",
    ],
    prepareEn: [
      "The destination's prescribed form or wording (banks, land registries and courts often have their own)",
      "The principal's original passport",
      "Full details of the attorney-in-fact and a clearly scoped mandate",
    ],
    pitfallsTh: [
      "ขอบเขตอำนาจกว้างหรือแคบเกินไปจนหน่วยงานปลายทางไม่รับ",
      "ลงนามล่วงหน้าก่อนพบทนาย — ต้องลงนามต่อหน้าเท่านั้น",
      "ไม่ได้ระบุวันหมดอายุหรือกฎหมายที่ใช้บังคับตามที่ปลายทางกำหนด",
    ],
    pitfallsEn: [
      "A mandate that is too broad or too narrow for the receiving authority",
      "Pre-signing the document — it must be signed in the attorney's presence",
      "Omitting an expiry date or governing-law clause the destination requires",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "company-affidavit",
    titleTh: "รับรองนิติกรณ์หนังสือรับรองบริษัท (DBD) และ บอจ.5",
    titleEn: "Legalisation of DBD Company Affidavits & Shareholder Lists",
    issuerTh: "หนังสือรับรองนิติบุคคลและบัญชีรายชื่อผู้ถือหุ้น (บอจ.5) ออกโดยกรมพัฒนาธุรกิจการค้า",
    issuerEn: "Company affidavit and shareholder list (Bor Or Jor 5) issued by the Department of Business Development",
    chainTh: [
      "คัดหนังสือรับรองฉบับล่าสุดจาก DBD (โดยทั่วไปปลายทางรับอายุไม่เกิน 3–6 เดือน)",
      "แปลไทย–อังกฤษพร้อมคำรับรองผู้แปล หรือขอฉบับภาษาอังกฤษจาก DBD",
      "นิติกรณ์กรมการกงสุล",
      "รับรองสถานทูตปลายทาง หรือ Apostille หลัง 28 ก.พ. 2570",
    ],
    chainEn: [
      "Obtain the latest affidavit from the DBD (destinations usually accept 3–6 months old)",
      "Thai–English translation with certificate of accuracy, or request the DBD English version",
      "MFA legalisation",
      "Destination embassy, or Apostille after 28 February 2027",
    ],
    useCaseTh: "ใช้เปิดบริษัทลูกหรือสาขาในต่างประเทศ เปิดบัญชีธนาคารนิติบุคคล ประมูลงาน จดเครื่องหมายการค้า และทำสัญญาการค้า",
    useCaseEn: "Setting up foreign subsidiaries or branches, corporate bank accounts, tenders, trademark filings and trade contracts.",
    prepareTh: [
      "หนังสือรับรองฉบับล่าสุดและ บอจ.5 ที่ตรงกับโครงสร้างผู้ถือหุ้นปัจจุบัน",
      "หนังสือมอบอำนาจกรรมการผู้มีอำนาจ พร้อมตราประทับบริษัท",
      "รายชื่อภาษาอังกฤษของกรรมการให้ตรงหนังสือเดินทาง",
    ],
    prepareEn: [
      "The latest affidavit and a Bor Or Jor 5 matching the current shareholding",
      "A director's power of attorney with the company seal",
      "English director names matching their passports",
    ],
    pitfallsTh: [
      "ใช้หนังสือรับรองเก่าเกิน 6 เดือน",
      "ชื่อบริษัทภาษาอังกฤษในคำแปลไม่ตรงกับที่จดทะเบียนไว้กับ DBD",
      "ไม่ได้แปลหมายเหตุ/วัตถุประสงค์ท้ายเอกสารที่ปลายทางต้องใช้",
    ],
    pitfallsEn: [
      "Using an affidavit older than six months",
      "An English company name that differs from the registered DBD name",
      "Leaving the objectives or endnotes untranslated when the destination needs them",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "commercial-invoice-co",
    titleTh: "รับรองนิติกรณ์เอกสารส่งออก (Invoice / Certificate of Origin)",
    titleEn: "Legalisation of Export Documents (Invoice & Certificate of Origin)",
    issuerTh: "Commercial Invoice, Packing List และ Certificate of Origin (ฟอร์ม C/O จากกรมการค้าต่างประเทศหรือหอการค้าไทย)",
    issuerEn: "Commercial invoice, packing list and Certificate of Origin (issued by the Department of Foreign Trade or the Thai Chamber of Commerce)",
    chainTh: [
      "ออก C/O จากกรมการค้าต่างประเทศหรือหอการค้าไทย",
      "รับรองเอกสารการค้าโดยหอการค้าตามที่ผู้ซื้อ/L\u2009/C กำหนด",
      "นิติกรณ์กรมการกงสุล",
      "รับรองสถานทูตประเทศผู้นำเข้า (โดยเฉพาะกลุ่มตะวันออกกลาง) หรือ Apostille หากปลายทางเป็นภาคีและยอมรับ",
    ],
    chainEn: [
      "Issue the C/O via the Department of Foreign Trade or the Thai Chamber of Commerce",
      "Chamber certification of trade documents as required by the buyer or the letter of credit",
      "MFA legalisation",
      "Importing-country embassy legalisation (common for Middle East markets), or an Apostille where the destination accepts one",
    ],
    useCaseTh: "ใช้พิธีการศุลกากรปลายทาง เบิกเงินตาม L/C ขอสิทธิพิเศษทางภาษี และประมูลงานภาครัฐต่างประเทศ",
    useCaseEn: "Destination customs clearance, letter-of-credit drawdowns, preferential tariff claims and foreign public tenders.",
    prepareTh: [
      "ชุดเอกสารการค้าที่ตัวเลขและน้ำหนักตรงกันทุกฉบับ",
      "หนังสือรับรองบริษัทและตัวอย่างลายมือชื่อผู้มีอำนาจลงนาม",
      "ข้อกำหนดของผู้ซื้อ/ธนาคารเรื่องจำนวนชุดที่ต้องรับรอง",
    ],
    prepareEn: [
      "A trade set whose figures and weights match across every document",
      "Company affidavit and specimen signatures of the authorised signatory",
      "The buyer's or bank's requirement for how many certified sets are needed",
    ],
    pitfallsTh: [
      "ตัวเลขใน Invoice ไม่ตรงกับ Packing List หรือ B/L",
      "ยื่นเวลากระชั้นจนไม่ทันรอบเรือ ควรเผื่อคิวสถานทูต 3–7 วันทำการ",
      "ประเทศผู้นำเข้าบางแห่งต้องรับรองหอการค้าก่อนกงสุลเสมอ",
    ],
    pitfallsEn: [
      "Invoice figures that disagree with the packing list or bill of lading",
      "Filing too close to the shipping date — allow 3–7 working days for embassy queues",
      "Some importing countries require chamber certification before the consular step",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "medical-certificate",
    titleTh: "รับรองนิติกรณ์ใบรับรองแพทย์และเอกสารทางการแพทย์",
    titleEn: "Legalisation of Medical Certificates & Records",
    issuerTh: "ใบรับรองแพทย์ ผลตรวจ และประวัติการรักษา ออกโดยโรงพยาบาลพร้อมเลขใบอนุญาตประกอบวิชาชีพเวชกรรม",
    issuerEn: "Medical certificates, test results and records issued by a hospital, showing the physician's licence number",
    chainTh: [
      "ขอใบรับรองแพทย์ฉบับภาษาอังกฤษพร้อมตราโรงพยาบาลและเลขใบอนุญาตแพทย์",
      "แปลหากออกเป็นภาษาไทย พร้อมคำรับรองผู้แปล",
      "นิติกรณ์กรมการกงสุล",
      "รับรองสถานทูตปลายทาง หรือ Apostille หลังอนุสัญญามีผล",
    ],
    chainEn: [
      "Request an English certificate bearing the hospital seal and the doctor's licence number",
      "Translate Thai-language documents with a certificate of accuracy",
      "MFA legalisation",
      "Destination embassy, or Apostille once in force",
    ],
    useCaseTh: "ใช้ยื่นวีซ่า เคลมประกันต่างประเทศ ส่งต่อการรักษา ขอใบอนุญาตทำงาน และเรื่องสิทธิประโยชน์",
    useCaseEn: "Visa filings, foreign insurance claims, continuity of care abroad, work permits and benefit claims.",
    prepareTh: [
      "ใบรับรองแพทย์ฉบับจริงที่ลงนามโดยแพทย์ผู้มีใบอนุญาต",
      "ระบุวัตถุประสงค์การใช้ให้โรงพยาบาลออกข้อความให้ตรง",
      "หนังสือยินยอมเปิดเผยข้อมูลผู้ป่วย หากให้ผู้อื่นดำเนินการแทน",
    ],
    prepareEn: [
      "An original certificate signed by a licensed physician",
      "Tell the hospital the exact purpose so the wording matches",
      "A patient consent letter if someone else handles the filing",
    ],
    pitfallsTh: [
      "ใบรับรองไม่มีเลขใบอนุญาตแพทย์หรือตราโรงพยาบาล",
      "เอกสารเกินอายุ (วีซ่าหลายประเภทรับไม่เกิน 3 เดือน)",
      "ข้อความไม่ครอบคลุมสิ่งที่ปลายทางต้องการ เช่น ผลตรวจเฉพาะโรค",
    ],
    pitfallsEn: [
      "No physician licence number or hospital seal",
      "An expired certificate (many visas accept only 3 months)",
      "Wording that omits the specific findings the destination needs",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
  D({
    slug: "death-certificate",
    titleTh: "รับรองนิติกรณ์มรณบัตรและเอกสารมรดก",
    titleEn: "Legalisation of Death Certificates & Estate Documents",
    issuerTh: "มรณบัตร (ท.ร.4) ฉบับคัดรับรองจากอำเภอ/เขต และคำสั่งศาลตั้งผู้จัดการมรดก (ถ้ามี)",
    issuerEn: "Death certificate (Tor Ror 4) certified extract, plus a court order appointing the estate administrator where applicable",
    chainTh: [
      "คัดมรณบัตรฉบับรับรองจากอำเภอ/เขต",
      "คัดคำสั่งศาลตั้งผู้จัดการมรดกพร้อมหนังสือรับรองคดีถึงที่สุด (ถ้าใช้)",
      "แปลพร้อมคำรับรองผู้แปล แล้วยื่นนิติกรณ์กรมการกงสุล",
      "รับรองสถานทูตปลายทาง หรือ Apostille หลัง 28 ก.พ. 2570",
    ],
    chainEn: [
      "Obtain a certified death certificate extract",
      "Obtain the court appointment order with certificate of finality if needed",
      "Translate with certificate of accuracy, then file for MFA legalisation",
      "Destination embassy, or Apostille after 28 February 2027",
    ],
    useCaseTh: "ใช้เคลมประกันชีวิตต่างประเทศ ปิดบัญชีธนาคาร โอนทรัพย์สินมรดก และแจ้งสถานะต่อหน่วยงานต่างประเทศ",
    useCaseEn: "Foreign life-insurance claims, closing bank accounts, transferring estate assets and notifying foreign authorities.",
    prepareTh: [
      "มรณบัตรฉบับคัดรับรองใหม่",
      "เอกสารแสดงความสัมพันธ์ทายาท เช่น สูติบัตร ทะเบียนสมรส ทะเบียนบ้าน",
      "คำสั่งศาลและหนังสือรับรองคดีถึงที่สุด กรณีมีผู้จัดการมรดก",
    ],
    prepareEn: [
      "A freshly certified death certificate",
      "Proof of relationship such as birth or marriage certificates and house registration",
      "The court order and certificate of finality where an administrator is appointed",
    ],
    pitfallsTh: [
      "ยื่นคำสั่งศาลโดยไม่มีหนังสือรับรองคดีถึงที่สุด",
      "เอกสารสายสัมพันธ์ทายาทไม่ครบชุดตามที่ปลายทางกำหนด",
      "ตัวสะกดชื่อผู้เสียชีวิตในเอกสารแต่ละฉบับไม่ตรงกัน",
    ],
    pitfallsEn: [
      "Filing a court order without the certificate of finality",
      "An incomplete chain of relationship documents",
      "Inconsistent spelling of the deceased's name across documents",
    ],
    apostilleNoteTh: APO_TH,
    apostilleNoteEn: APO_EN,
    turnaroundTh: COMMON_TURN_TH,
    turnaroundEn: COMMON_TURN_EN,
  }),
];

export const CONSULAR_DOC_SLUGS = CONSULAR_DOC_LANDINGS.map((d) => d.slug);

export function getConsularDocLanding(slug: string): ConsularDocLanding | undefined {
  return CONSULAR_DOC_LANDINGS.find((d) => d.slug === slug);
}
